import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { 
  LuCoins, LuTrendingUp, LuTrendingDown, LuWallet, 
  LuChartBar, LuCalendar, LuDownload, LuPlus, LuX 
} from "react-icons/lu";

// Importações do Firebase
import { 
  getFirestore, collection, query, where, getDocs, 
  orderBy, Timestamp, limit, addDoc 
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "../firebase"; // Certifique-se que este caminho está correto no seu projeto
import LoadingComLayout from "../components/LoadingComLayout"; // Ajuste o caminho se necessário

// Inicializa serviços
const db = getFirestore(app);
const auth = getAuth(app);

/* ===================== ESTILOS ===================== */
const Main = styled.main`
  flex: 1; padding: 2rem 4rem; background: #f3f7ff; min-height: 100vh;
  display: flex; flex-direction: column; gap: 1.25rem;
  @media (max-width: 1280px){ padding: 2rem; }
  @media (max-width: 768px){ padding: 1rem; }
`;
const Grid = styled.div`
  display: grid; gap: 1rem; grid-template-columns: 2fr 1fr;
  @media (max-width: 1024px){ grid-template-columns: 1fr; }
`;
const Card = styled.section`
  background: #fff; border: 1px solid #e6ecf5; border-radius: 1rem;
  box-shadow: 0 6px 20px rgba(58,121,237,.06); position: relative;
  display: flex; flex-direction: column;
`;
const CardHeader = styled.div`
  display:flex; align-items:center; gap:.6rem; justify-content: space-between;
  padding: 1rem 1.1rem; border-bottom: 1px solid #eef2fb; color: #1f1a40; font-weight: 700;
  div { display: flex; align-items: center; gap: .6rem; }
  svg { color:#3a79ed; }
`;
const CardBody = styled.div`
  padding: 1rem 1.1rem; display:flex; flex-direction:column; gap:.9rem; flex: 1;
`;
const Button = styled.button`
  align-self:flex-start; display:inline-flex; align-items:center; gap:.6rem;
  padding:.75rem 1rem; font-weight:800; font-size:.95rem; color:#fff; background:#3a79ed; 
  border:0; border-radius:.75rem; box-shadow:0 4px 12px rgba(58,121,237,.28);
  cursor:pointer; transition: transform .15s ease, box-shadow .15s ease, opacity .2s ease;
  &:hover{ transform: translateY(-1px); box-shadow:0 6px 16px rgba(58,121,237,.34); }
  &:disabled{ opacity:.6; cursor:not-allowed; }
`;
const Secondary = styled(Button)`
  background:#1f1a40; box-shadow:0 4px 12px rgba(31,26,64,.2);
  width: 100%; justify-content: center;
`;
const DangerButton = styled(Button)`
  background:#b42318; box-shadow:0 4px 12px rgba(180, 35, 24,.2);
  width: 100%; justify-content: center;
`;
const Helper = styled.p`margin:0; color:#5b6478; font-size:.9rem;`;
const StatGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;
`;
const StatBox = styled.div`
  background: #f8fbff; border: 1px solid #dfe6f6; border-radius: .75rem;
  padding: 1rem; display: flex; flex-direction: column; gap: 0.5rem;
  h4 { margin: 0; font-size: 0.85rem; color: #5b6478; font-weight: 600; text-transform: uppercase; }
  .value { font-size: 1.5rem; font-weight: 800; color: #1f1a40; }
  .trend {
    font-size: 0.8rem; font-weight: 700; display: flex; align-items: center; gap: 0.3rem;
    color: ${p => p.positive ? "#157347" : p.neutral ? "#5b6478" : "#b42318"};
  }
`;
const ListRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.75rem 0; border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
  span.name { color: #334155; font-weight: 600; font-size: 0.95rem; }
  span.val { color: #e5484d; font-weight: 700; font-size: 0.95rem; }
`;
const ChartPlaceHolder = styled.div`
  background: #f8fbff; border: 1px dashed #dfe6f6; border-radius: .75rem;
  height: 250px; display: flex; align-items: center; justify-content: center;
  color: #94a3b8; font-style: italic;
`;
// --- ESTILOS DO MODAL ---
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center;
  z-index: 1000;
`;
const ModalContent = styled.div`
  background: white; padding: 2rem; border-radius: 1rem; width: 90%; max-width: 400px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
`;
const TitleRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;
  h3 { margin: 0; color: #1f1a40; }
  cursor: pointer;
`;
const InputGroup = styled.div`
  display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem;
  label { font-size: 0.9rem; font-weight: 600; color: #334155; }
  input, select {
    padding: 0.75rem; border: 1px solid #dfe6f6; border-radius: 0.5rem;
    outline: none; font-size: 1rem;
    &:focus { border-color: #3a79ed; }
  }
`;

// --- NOVOS ESTILOS PARA TABELA ---
const TableContainer = styled.div`
  overflow-x: auto;
  max-height: 400px; /* Limita a altura para que a tabela tenha scroll interno */
`;
const TransactionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  text-align: left;
`;
const THead = styled.thead`
  background: #f8fbff;
  tr th {
    padding: 0.75rem 1rem;
    color: #5b6478;
    font-weight: 700;
    text-transform: uppercase;
    border-bottom: 2px solid #eef2fb;
  }
`;
const TBody = styled.tbody`
  tr {
    border-bottom: 1px solid #f0f0f0;
    &:last-child {
      border-bottom: none;
    }
  }
  tr td {
    padding: 0.75rem 1rem;
    color: #334155;
    white-space: nowrap;
    max-width: 150px; /* Limita largura para quebra de linha em descrição */
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const ValueCell = styled.td`
  font-weight: 700;
  color: ${props => props.type === 'entrada' ? '#166534' : '#b42318'};
`;
const TypeCell = styled.span`
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.type === 'entrada' ? '#065f46' : '#9a3412'};
  background: ${props => props.type === 'entrada' ? '#d1fae5' : '#fee2e2'};
`;

/* ===================== LÓGICA ===================== */

function formatCurrency(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
}

// NOVO: Função para formatar data e hora para a tabela
function formatDateTime(date) {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' +
           d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function Faturamento() {
  const [loading, setLoading] = useState(true);
  const [barbeariaId, setBarbeariaId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [dados, setDados] = useState({
    receitaMensal: 0,
    despesaMensal: 0,
    lucroMensal: 0,
    ticketMedio: 0,
    percentualLucro: 0,
    despesas: [],
    transacoesCount: 0
  });

  const [transacoesRelatorio, setTransacoesRelatorio] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [novaDespesa, setNovaDespesa] = useState({
    valor: "",
    descricao: "",
    categoria: "Outros"
  });
  const [salvando, setSalvando] = useState(false);

  // --- BUSCA DADOS ---
  const fetchFinancialData = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }

      let currentBarbeariaId = barbeariaId;
      if (!currentBarbeariaId) {
        const qBarbearia = query(collection(db, "barbearia"), where("criadorUid", "==", user.uid), limit(1));
        const barbeariaSnap = await getDocs(qBarbearia);
        if (barbeariaSnap.empty) { setLoading(false); return; }
        currentBarbeariaId = barbeariaSnap.docs[0].id;
        setBarbeariaId(currentBarbeariaId);
      }

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const transacoesRef = collection(db, "barbearia", currentBarbeariaId, "transacoes");
      const q = query(
        transacoesRef,
        where("data", ">=", Timestamp.fromDate(startOfMonth)),
        where("data", "<=", Timestamp.fromDate(endOfMonth)),
        orderBy("data", "desc")
      );

      const querySnapshot = await getDocs(q);
      
      let receita = 0;
      let despesa = 0;
      let countVendas = 0;
      const despesasMap = {};
      const listaParaCSV = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const valor = parseFloat(data.valor || 0);
        
        listaParaCSV.push({
          data: data.data ? data.data.toDate() : new Date(),
          tipo: data.tipo, 
          categoria: data.categoria || '-',
          descricao: data.descricao || 'Sem descrição',
          valor: valor
        });

        if (data.tipo === 'entrada') {
          receita += valor;
          countVendas++;
        } else if (data.tipo === 'saida') {
          despesa += valor;
          const cat = data.categoria || "Outros";
          if (!despesasMap[cat]) despesasMap[cat] = 0;
          despesasMap[cat] += valor;
        }
      });

      setTransacoesRelatorio(listaParaCSV);

      const listaDespesas = Object.keys(despesasMap)
        .map(key => ({ nome: key, valor: despesasMap[key] }))
        .sort((a, b) => b.valor - a.valor)
        .slice(0, 5);

      setDados({
        receitaMensal: receita,
        despesaMensal: despesa,
        lucroMensal: receita - despesa,
        ticketMedio: countVendas > 0 ? receita / countVendas : 0,
        percentualLucro: receita > 0 ? ((receita - despesa) / receita) : 0,
        despesas: listaDespesas,
        transacoesCount: countVendas
      });

    } catch (error) {
      console.error("Erro ao buscar dados financeiros:", error);
    } finally {
      setLoading(false);
    }
  }, [barbeariaId]);

  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData, refreshTrigger]);

  // --- ADICIONAR DESPESA (Lógica mantida) ---
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!novaDespesa.valor || !barbeariaId) return;

    setSalvando(true);
    try {
      await addDoc(collection(db, "barbearia", barbeariaId, "transacoes"), {
        tipo: 'saida',
        valor: parseFloat(novaDespesa.valor),
        descricao: novaDespesa.descricao,
        categoria: novaDespesa.categoria,
        data: Timestamp.now(),
        usuarioUid: auth.currentUser.uid
      });

      setNovaDespesa({ valor: "", descricao: "", categoria: "Outros" });
      setIsModalOpen(false);
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error("Erro ao salvar despesa:", error);
      alert("Erro ao salvar. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  // --- EXPORTAR CSV (Lógica mantida) ---
  const handleExportCSV = () => {
    if (transacoesRelatorio.length === 0) {
      alert("Não há dados para exportar neste mês.");
      return;
    }
    let csvContent = "Data;Hora;Tipo;Categoria;Descrição;Valor\n";
    transacoesRelatorio.forEach(item => {
      const dataStr = item.data.toLocaleDateString("pt-BR");
      const horaStr = item.data.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });
      const valorStr = item.valor.toString().replace(".", ",");
      const safeDesc = item.descricao.replace(/;/g, " ").replace(/\n/g, " ");

      csvContent += `${dataStr};${horaStr};${item.tipo.toUpperCase()};${item.categoria};${safeDesc};${valorStr}\n`;
    });

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const mesNome = new Date().toLocaleString('pt-BR', { month: 'long' });
    link.setAttribute("href", url);
    link.setAttribute("download", `financeiro_${mesNome}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <LoadingComLayout />;

  return (
    <>
      <Main>
        <Grid>
          {/* COLUNA ESQUERDA: GRÁFICOS E KPI */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            
            {/* 1. VISÃO GERAL */}
            <Card>
              <CardHeader>
                <div><LuCoins /> Visão Geral do Mês</div>
              </CardHeader>
              <CardBody>
                <StatGrid>
                  <StatBox positive={true}>
                    <h4>Receita Bruta</h4>
                    <div className="value">{formatCurrency(dados.receitaMensal)}</div>
                    <div className="trend"><LuTrendingUp /> Entradas</div>
                  </StatBox>

                  <StatBox positive={false}>
                    <h4>Despesas</h4>
                    <div className="value" style={{color: '#b42318'}}>
                      {formatCurrency(dados.despesaMensal)}
                    </div>
                    <div className="trend" style={{color: '#b42318'}}><LuTrendingDown /> Saídas</div>
                  </StatBox>

                  <StatBox neutral>
                    <h4>Lucro Real</h4>
                    <div className="value" style={{ color: dados.lucroMensal >= 0 ? '#166534' : '#b42318' }}>
                      {formatCurrency(dados.lucroMensal)}
                    </div>
                    <div className="trend">Margem {(dados.percentualLucro * 100).toFixed(1)}%</div>
                  </StatBox>

                  <StatBox neutral>
                    <h4>Ticket Médio</h4>
                    <div className="value">{formatCurrency(dados.ticketMedio)}</div>
                    <div className="trend">{dados.transacoesCount} vendas</div>
                  </StatBox>
                </StatGrid>
              </CardBody>
            </Card>

            {/* 2. GRÁFICO (Placeholder) */}
            <Card>
              <CardHeader>
                <div><LuChartBar /> Evolução Financeira</div>
              </CardHeader>
              <CardBody>
                <ChartPlaceHolder>
                  <div style={{textAlign: 'center', color: '#94a3b8'}}>
                    <p>Lucro Líquido: <strong>{formatCurrency(dados.lucroMensal)}</strong></p>
                    <small>Gráfico será implementado em breve</small>
                  </div>
                </ChartPlaceHolder>
              </CardBody>
            </Card>

            {/* 3. TABELA DE TRANSAÇÕES RECENTES (NOVA) */}
            <Card>
              <CardHeader>
                <div><LuCalendar /> Transações Recentes</div>
              </CardHeader>
              <CardBody style={{ padding: '0' }}>
                {transacoesRelatorio.length > 0 ? (
                    <TableContainer>
                        <TransactionTable>
                            <THead>
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Tipo</th>
                                    <th>Descrição</th>
                                    <th>Valor</th>
                                </tr>
                            </THead>
                            <TBody>
                                {transacoesRelatorio.slice(0, 10).map((t, index) => (
                                    <tr key={index}>
                                        <td>{formatDateTime(t.data)}</td>
                                        <td>
                                            <TypeCell type={t.tipo}>
                                                {t.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                                            </TypeCell>
                                        </td>
                                        <td>{t.descricao} ({t.categoria})</td>
                                        <ValueCell type={t.tipo}>
                                            {t.tipo === 'entrada' ? '+' : '-'} {formatCurrency(t.valor)}
                                        </ValueCell>
                                    </tr>
                                ))}
                            </TBody>
                        </TransactionTable>
                    </TableContainer>
                ) : (
                    <p style={{textAlign:'center', color:'#94a3b8', padding: '1.5rem'}}>
                        Nenhuma transação registrada neste mês.
                    </p>
                )}
              </CardBody>
            </Card>


          </div>

          {/* COLUNA DIREITA: AÇÕES E LISTAS */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            
            {/* 4. CONTROLE DE GASTOS */}
            <Card>
              <CardHeader>
                <div><LuTrendingDown /> Controle de Gastos</div>
              </CardHeader>
              <CardBody>
                <Helper>Maiores despesas do mês:</Helper>
                <div style={{ margin: '1rem 0' }}>
                  {dados.despesas.length > 0 ? (
                    dados.despesas.map((d, i) => (
                      <ListRow key={i}>
                        <span className="name">{d.nome}</span>
                        <span className="val">- {formatCurrency(d.valor)}</span>
                      </ListRow>
                    ))
                  ) : (
                    <p style={{textAlign:'center', color:'#94a3b8', fontSize:'0.9rem'}}>Nenhuma despesa registrada.</p>
                  )}
                </div>
                <DangerButton onClick={() => setIsModalOpen(true)}>
                  <LuPlus /> Adicionar Despesa
                </DangerButton>
              </CardBody>
            </Card>

            {/* 5. RELATÓRIOS */}
            <Card>
              <CardHeader>
                <div><LuWallet /> Relatórios</div>
              </CardHeader>
              <CardBody>
                <Helper>{transacoesRelatorio.length} movimentações encontradas neste mês.</Helper>
                <Secondary onClick={handleExportCSV} disabled={transacoesRelatorio.length === 0}>
                   <LuDownload /> Baixar Planilha (CSV)
                </Secondary>
              </CardBody>
            </Card>

          </div>
        </Grid>
      </Main>

      {/* --- MODAL --- */}
      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <TitleRow>
              <h3>Nova Despesa</h3>
              <LuX size={24} onClick={() => setIsModalOpen(false)} />
            </TitleRow>
            
            <form onSubmit={handleAddExpense}>
              <InputGroup>
                <label>Valor (R$)</label>
                <input 
                  type="number" 
                  step="0.01"
                  placeholder="0,00" 
                  value={novaDespesa.valor}
                  onChange={e => setNovaDespesa({...novaDespesa, valor: e.target.value})}
                  required
                  autoFocus
                />
              </InputGroup>

              <InputGroup>
                <label>Descrição</label>
                <input 
                  type="text" 
                  placeholder="Ex: Conta de Luz, Café..." 
                  value={novaDespesa.descricao}
                  onChange={e => setNovaDespesa({...novaDespesa, descricao: e.target.value})}
                  required
                />
              </InputGroup>

              <InputGroup>
                <label>Categoria</label>
                <select 
                  value={novaDespesa.categoria}
                  onChange={e => setNovaDespesa({...novaDespesa, categoria: e.target.value})}
                >
                  <option value="Outros">Outros</option>
                  <option value="Produtos">Produtos/Estoque</option>
                  <option value="Aluguel">Aluguel</option>
                  <option value="Energia">Energia/Água</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Equipe">Comissões</option>
                  <option value="Manutencao">Manutenção</option>
                </select>
              </InputGroup>

              <DangerButton type="submit" disabled={salvando} style={{marginTop: '1rem', width: '100%'}}>
                {salvando ? "Salvando..." : "Confirmar Despesa"}
              </DangerButton>
            </form>

          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}